// Legacy exception, not the preferred pattern: preserve this row's edit-button sizing
// until its layout is checked with the internal IconButton. Do not copy this import.
// nosemgrep: no-mui-icon-button
import { IconButton } from '@mui/material';
import { useMemo } from 'react';
import { useController, useWatch } from 'react-hook-form';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { LoadingAdornment } from '@/components/form/baseInputs/SelectInput/LoadingAdornment';
import { TypeaheadSelectInput } from '@/components/form/baseInputs/TypeaheadSelectInput/TypeaheadSelectInput';
import { useFormFieldsDisabled } from '@/components/form/context/formFieldsDisabled.context';
import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareTypeaheadSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { Edit02Icon } from '@/components/icons/Edit02Icon';
import { useFormContext } from '@/components/react-hook-form';
import { PrimaryCellTypography } from '@/components/tables/DataTable/components/cells';
import { StyledTableCell } from '@/components/tables/DisplayTable/DisplayTable';
import { StyledTableRow } from '@/components/tables/DisplayTable/StyledTableRow';
import { InsurancePolicyTypeCopyMap } from '@/modules/entities/InsurancePolicyDetailsSubform/InsurancePolicyDetailsSubform.types';
import { InsurancePolicyKind } from '@/types/schema';

import { LifeInsuranceStepFormShape } from './LifeInsuranceStep.types';

const POLICY_TYPE_OPTIONS = [
  { display: 'Term', value: InsurancePolicyKind.Term },
  { display: 'Whole', value: InsurancePolicyKind.Whole },
  { display: 'Universal', value: InsurancePolicyKind.Universal },
  { display: 'Variable', value: InsurancePolicyKind.Variable },
];

interface LifeInsuranceStepRowProps {
  policyId: string;
  name: string;
  deathBenefitAmount: string | null;
  ownerEntityOptions: TypeaheadSelectInputOption<string>[];
  insuredIndividualOptions?: TypeaheadSelectInputOption<string>[];
  isPolicyTypeEditable?: boolean;
  isInferring?: boolean;
  onEdit: (policyId: string) => void;
  importTableLayout?: boolean;
  /**
   * When true, the row's details are only editable once it's checked for
   * import — the fields describe how to import the row, so they mean nothing
   * until it is. Off by default to preserve the legacy trowser's behavior.
   */
  disableUntilSelected?: boolean;
}

export function LifeInsuranceStepRow({
  policyId,
  name,
  deathBenefitAmount,
  ownerEntityOptions,
  insuredIndividualOptions = [],
  isPolicyTypeEditable = false,
  isInferring = false,
  onEdit,
  importTableLayout = false,
  disableUntilSelected = false,
}: LifeInsuranceStepRowProps) {
  const { control } = useFormContext<LifeInsuranceStepFormShape>();

  const shouldImport = useWatch({
    control,
    name: `policies.${policyId}.shouldImport`,
  });
  const policyType = useWatch({
    control,
    name: `policies.${policyId}.policyType`,
  });
  const areDetailsDisabled = disableUntilSelected && !shouldImport;

  return (
    <StyledTableRow>
      <StyledTableCell width={40} align="center">
        <FormAwareCheckbox<LifeInsuranceStepFormShape>
          control={control}
          fieldName={`policies.${policyId}.shouldImport`}
          hideLabel
          // Alone in its column, so the row's name is the only thing that can
          // give it an accessible name.
          ariaLabel={`Import ${name}`}
        />
      </StyledTableCell>

      <StyledTableCell>
        <PrimaryCellTypography noWrap title={name}>
          {name}
        </PrimaryCellTypography>
      </StyledTableCell>

      {importTableLayout && !isPolicyTypeEditable && policyType ? (
        <StyledTableCell>
          <PrimaryCellTypography>
            {InsurancePolicyTypeCopyMap[policyType]}
          </PrimaryCellTypography>
        </StyledTableCell>
      ) : (
        <StyledTableCell>
          <FormAwareSelectInput<LifeInsuranceStepFormShape>
            control={control}
            fieldName={`policies.${policyId}.policyType`}
            label="Policy type"
            hideLabel
            options={POLICY_TYPE_OPTIONS}
            showEmptyValue
            emptyValueDisplay="Select type"
            disabled={areDetailsDisabled}
            startAdornment={isInferring ? <LoadingAdornment /> : undefined}
          />
        </StyledTableCell>
      )}

      <StyledTableCell>
        <FormAwareTypeaheadSelectInput<LifeInsuranceStepFormShape>
          control={control}
          fieldName={`policies.${policyId}.ownerEntityId`}
          label="Owner"
          hideLabel
          options={ownerEntityOptions}
          groupBy={(option) => option.groupName ?? ''}
          placeholder="Select owner"
          disabled={areDetailsDisabled}
        />
      </StyledTableCell>

      {importTableLayout && (
        <StyledTableCell>
          <InsuredIndividualsInput
            policyId={policyId}
            personOptions={insuredIndividualOptions}
            allowTwoPeople={
              !!policyType && policyType !== InsurancePolicyKind.Term
            }
            disabled={areDetailsDisabled}
            isInferring={isInferring}
          />
        </StyledTableCell>
      )}

      <StyledTableCell align="right">
        <PrimaryCellTypography>
          {deathBenefitAmount ?? '—'}
        </PrimaryCellTypography>
      </StyledTableCell>

      {importTableLayout && (
        <StyledTableCell>
          <PrimaryCellTypography>9 other fields</PrimaryCellTypography>
        </StyledTableCell>
      )}

      <StyledTableCell align="center" width={48}>
        <IconButton
          aria-label={`Edit ${name}`}
          size="small"
          onClick={() => onEdit(policyId)}
          disabled={areDetailsDisabled}
        >
          <Edit02Icon />
        </IconButton>
      </StyledTableCell>
    </StyledTableRow>
  );
}

interface InsuredIndividualsInputProps {
  policyId: string;
  personOptions: TypeaheadSelectInputOption<string>[];
  allowTwoPeople: boolean;
  disabled: boolean;
  isInferring: boolean;
}

function getInsuredIndividualsOptionValue(holderIDs: string[]) {
  return JSON.stringify(holderIDs);
}

function InsuredIndividualsInput({
  policyId,
  personOptions,
  allowTwoPeople,
  disabled,
  isInferring,
}: InsuredIndividualsInputProps) {
  const { control } = useFormContext<LifeInsuranceStepFormShape>();
  const { disabled: disabledFromContext } = useFormFieldsDisabled();
  const { field, fieldState } = useController({
    control,
    name: `policies.${policyId}.policyHolderIDs`,
  });

  const { options, holderIDsByValue } = useMemo(() => {
    const individualEntries = personOptions.map((option) => ({
      display: option.display,
      holderIDs: [option.value],
    }));

    const pairedEntries = allowTwoPeople
      ? personOptions.flatMap((firstOption, firstIndex) =>
          personOptions.slice(firstIndex + 1).map((secondOption) => ({
            display: `${firstOption.display} and ${secondOption.display}`,
            holderIDs: [firstOption.value, secondOption.value],
          }))
        )
      : [];
    const entries = [...individualEntries, ...pairedEntries];

    // The typeahead selects one string, but the form stores one or two IDs.
    // Use the serialized ID array as the option key, then map it back without
    // parsing input from the component.
    return {
      options: entries.map((entry) => ({
        display: entry.display,
        value: getInsuredIndividualsOptionValue(entry.holderIDs),
      })),
      holderIDsByValue: new Map(
        entries.map((entry) => [
          getInsuredIndividualsOptionValue(entry.holderIDs),
          entry.holderIDs,
        ])
      ),
    };
  }, [allowTwoPeople, personOptions]);

  const selectedValue = getInsuredIndividualsOptionValue(
    (field.value ?? []).filter(Boolean)
  );

  return (
    <TypeaheadSelectInput
      id={field.name}
      name={field.name}
      label="Insured individuals"
      hideLabel
      options={options}
      value={selectedValue === '[]' ? '' : selectedValue}
      placeholder="Select insured individuals"
      disabled={disabledFromContext ?? disabled}
      startAdornment={isInferring ? <LoadingAdornment /> : undefined}
      error={!!fieldState.error}
      errorMessage={fieldState.error?.message}
      inputRef={field.ref}
      onBlur={field.onBlur}
      onChange={(_event, value) => {
        field.onChange(holderIDsByValue.get(value ?? '') ?? []);
      }}
    />
  );
}
